// CancellationsPage — two-tab queue for cancellation_requested + cancelled
// bookings. Pending Review is the action queue (Confirm / Reject); All
// Cancellations is the historical log with date/driver/search filters.
//
// Tab state lives in URL ?tab=pending|all so deep links stay stable.
// Filter state for the All tab lives in URL params per CLIENT-DECISION-010.

import { useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Eye, Link2, RotateCcw, ShieldX, Undo2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Badge } from '../../components/ui/Badge/Badge';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { CancelBookingModal } from '../bookings/components/CancelBookingModal';
import { DeclineCancellationModal } from './components/DeclineCancellationModal';
import { ReinstateBookingModal } from './components/ReinstateBookingModal';
import {
  useCancellationsAll,
  useCancellationsPending,
  useCompletedAfterCancellation,
} from './hooks/useCancellations';
import { useActiveDrivers } from '../bookings/hooks/useDriversList';
import {
  acknowledgeCompletedAfterCancellation,
  generateCancellationLink,
  readCancellationLink,
} from '../../api/bookings';
import { useToast } from '../../hooks/useToast';
import { queryKeys } from '../../api/queryKeys';
import type { BookingSummary } from '../../types/api';
import styles from './CancellationsPage.module.css';

const DEFAULT_LIMIT = 50;

type Tab = 'pending' | 'all' | 'conflicts';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function CancellationsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Copy a usable cancellation URL to the clipboard for a pending-review
   * row. `readCancellationLink` only reports existence (the raw token is
   * never re-readable post-issuance — the server stores only the hash),
   * so we issue a fresh link via POST whenever one isn't already active.
   * Per server D2 this also invalidates any prior unused token, which is
   * the safe default — the staff member is about to share a new link.
   */
  async function handleCopyCancelLink(row: BookingSummary) {
    try {
      let urlToCopy: string | null = null;
      const existing = await readCancellationLink(row.enc_id);
      if (existing === null) {
        // No active link — issue one now.
        const fresh = await generateCancellationLink(row.enc_id);
        urlToCopy = fresh.url;
      } else {
        // A link exists but its raw value can no longer be retrieved —
        // rotate so we have something usable to copy.
        const fresh = await generateCancellationLink(row.enc_id);
        urlToCopy = fresh.url;
      }
      if (urlToCopy === null) {
        toast.error('Could not generate cancellation link.');
        return;
      }
      if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(urlToCopy);
        toast.success('Cancellation link copied to clipboard');
      } else {
        toast.error('Clipboard is not available — copy the link manually.');
      }
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
          ?? 'Could not copy cancellation link.'
        : 'Could not copy cancellation link.';
      toast.error(message);
    }
  }

  const tabParam = params.get('tab');
  const tab: Tab = tabParam === 'all' ? 'all' : tabParam === 'conflicts' ? 'conflicts' : 'pending';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const limit = Math.max(
    1,
    Number.parseInt(params.get('limit') ?? String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT,
  );

  const driversQuery = useActiveDrivers();
  const driverOptions = useMemo(
    () =>
      [{ value: '', label: 'All drivers' }].concat(
        (driversQuery.data ?? []).map((d) => ({
          value: String(d.id),
          label: d.name,
        })),
      ),
    [driversQuery.data],
  );

  // Pending Review tab — no filters per the brief.
  const pendingQuery = useCancellationsPending({ page, limit });

  // All tab — driver/dates/search filters.
  const allFilters = useMemo(() => {
    const driverParam = params.get('driver');
    const dateFrom = params.get('date_from') ?? undefined;
    const dateTo = params.get('date_to') ?? undefined;
    const q = params.get('q') ?? undefined;
    return {
      page,
      limit,
      driver: driverParam ? Number(driverParam) : undefined,
      date_from: dateFrom,
      date_to: dateTo,
      q,
    };
  }, [params, page, limit]);

  const allQuery = useCancellationsAll(allFilters);

  // Completed-after-cancellation tab — receipts that completed a cancelled booking.
  const conflictsQuery = useCompletedAfterCancellation({ page, limit });
  const conflictsCount = conflictsQuery.data?.pagination.total ?? 0;

  const activeQuery =
    tab === 'pending' ? pendingQuery : tab === 'conflicts' ? conflictsQuery : allQuery;
  const data = activeQuery.data?.bookings ?? [];
  const total = activeQuery.data?.pagination.total ?? 0;
  const totalPages = activeQuery.data?.pagination.totalPages ?? 1;

  const pendingCount = pendingQuery.data?.pagination.total ?? 0;

  // ---- Actions ------------------------------------------------------------

  const [confirmTarget, setConfirmTarget] = useState<BookingSummary | null>(null);
  const [rejectTarget, setRejectTarget] = useState<BookingSummary | null>(null);
  const [reinstateTarget, setReinstateTarget] = useState<BookingSummary | null>(null);

  async function handleDeclineSuccess() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.cancellations.list({}) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    setRejectTarget(null);
  }

  async function handleReinstateSuccess() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.cancellations.list({}) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    setReinstateTarget(null);
  }

  async function handleAcknowledge(row: BookingSummary) {
    try {
      await acknowledgeCompletedAfterCancellation(row.enc_id);
      toast.success('Marked as reviewed');
      await queryClient.invalidateQueries({ queryKey: queryKeys.cancellations.list({}) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? 'Could not acknowledge.'
        : 'Could not acknowledge.';
      toast.error(message);
    }
  }

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  function selectTab(next: Tab) {
    const params2 = new URLSearchParams(params);
    params2.set('tab', next);
    params2.delete('page');
    setParams(params2, { replace: false });
  }

  // ---- Columns ------------------------------------------------------------

  const columns: Column<BookingSummary>[] = useMemo(() => {
    const cols: Column<BookingSummary>[] = [
      {
        key: 'date',
        header: 'Date',
        render: (_v, row) => formatDate(row.date),
      },
      {
        key: 'driver_name',
        header: 'Driver',
        render: (_v, row) => row.driver_name ?? '—',
      },
      {
        key: 'car_registration',
        header: 'Registration',
        render: (_v, row) => (
          <span className={styles.regChip}>{row.car_registration}</span>
        ),
      },
      {
        key: 'name',
        header: 'Customer',
        render: (_v, row) => (
          <div className={styles.cellStack}>
            <span>{row.name}</span>
            <span className={styles.cellMuted}>{row.phone}</span>
          </div>
        ),
      },
      {
        key: 'cancellation_reason',
        header: 'Reason',
        render: (_v, row) => row.cancellation_reason ?? '—',
      },
      {
        key: 'updated_at',
        header: 'Cancelled By',
        render: (_v, row) =>
          row.cancelled_at
            ? formatDate(row.cancelled_at)
            : row.updated_at
              ? formatDate(row.updated_at)
              : '—',
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) =>
          row.completed_after_cancellation === true ? (
            <Badge variant="warning">Completed (was cancelled)</Badge>
          ) : (
            <Badge variant={row.status === 'cancelled' ? 'danger' : 'warning'}>
              {row.status === 'cancelled' ? 'Cancelled' : 'Cancellation requested'}
            </Badge>
          ),
      },
    ];
    return cols;
  }, []);

  const rowActions = (row: BookingSummary): RowAction<BookingSummary>[] => {
    const acts: RowAction<BookingSummary>[] = [
      {
        label: 'View booking',
        icon: Eye,
        onClick: () => navigate(`/collections/bookings/${encodeURIComponent(row.enc_id)}`),
      },
    ];
    if (tab === 'conflicts') {
      acts.unshift({
        label: 'Mark as reviewed',
        icon: CheckCheck,
        variant: 'secondary',
        onClick: () => void handleAcknowledge(row),
      });
    } else if (tab === 'pending') {
      acts.unshift({
        label: 'Confirm cancellation',
        icon: ShieldX,
        variant: 'danger',
        onClick: () => setConfirmTarget(row),
      });
      acts.splice(1, 0, {
        label: 'Reject',
        icon: Undo2,
        variant: 'secondary',
        onClick: () => setRejectTarget(row),
      });
      acts.push({
        label: 'Copy cancel link',
        icon: Link2,
        variant: 'secondary',
        onClick: () => void handleCopyCancelLink(row),
      });
    } else if (row.status === 'cancelled') {
      // F2 — All Cancellations tab: revive a cancelled booking with a
      // fresh schedule via the ReinstateBookingModal.
      acts.unshift({
        label: 'Reinstate',
        icon: RotateCcw,
        variant: 'secondary',
        onClick: () => setReinstateTarget(row),
      });
    }
    return acts;
  };

  // ---- Render -------------------------------------------------------------

  return (
    <div className={styles.page}>
      <PageHeader
        title={
          <span className={styles.titleRow}>
            Cancellations
            {pendingCount > 0 ? (
              <Badge variant="warning" className={styles.countBadge}>
                {pendingCount}
              </Badge>
            ) : null}
          </span>
        }
        subtitle="Review and resolve cancellation requests"
      />

      <div role="tablist" className={styles.tabs} aria-label="Cancellations tabs">
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'pending'}
          tabIndex={tab === 'pending' ? 0 : -1}
          className={tab === 'pending' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('pending')}
        >
          Pending Review
          {pendingCount > 0 ? (
            <span className={styles.tabBadge}>{pendingCount}</span>
          ) : null}
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'all'}
          tabIndex={tab === 'all' ? 0 : -1}
          className={tab === 'all' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('all')}
        >
          All Cancellations
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'conflicts'}
          tabIndex={tab === 'conflicts' ? 0 : -1}
          className={tab === 'conflicts' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('conflicts')}
        >
          Completed after cancellation
          {conflictsCount > 0 ? (
            <span className={styles.tabBadge}>{conflictsCount}</span>
          ) : null}
        </button>
      </div>

      <Card flush>
        {tab === 'all' ? (
          <FilterBar
            search={{
              value: params.get('q') ?? '',
              onChange: (v) => setParam('q', v),
              placeholder: 'Search by customer or registration…',
              ariaLabel: 'Search cancellations',
            }}
          >
            <Select
              options={driverOptions}
              value={params.get('driver') ?? ''}
              onChange={(e) => setParam('driver', e.target.value)}
              aria-label="Filter by driver"
            />
            <DatePicker
              value={params.get('date_from') ?? ''}
              onChange={(e) => setParam('date_from', e.target.value)}
              aria-label="From date"
            />
            <DatePicker
              value={params.get('date_to') ?? ''}
              onChange={(e) => setParam('date_to', e.target.value)}
              aria-label="To date"
            />
          </FilterBar>
        ) : null}

        <Table
          columns={columns}
          data={data}
          keyField={(row) => row.enc_id}
          loading={activeQuery.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>
                {tab === 'pending'
                  ? 'No cancellation requests'
                  : tab === 'conflicts'
                    ? 'Nothing to review'
                    : 'No cancellations to show'}
              </h3>
              <p>
                {tab === 'pending'
                  ? 'Customer cancellation requests will appear here for review.'
                  : tab === 'conflicts'
                    ? 'Bookings a driver collected after they were cancelled will appear here. The booking is completed; this is for you to check.'
                    : 'Cancelled bookings will appear here once cancellations are confirmed.'}
              </p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Cancellations"
        />
        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      {/* Confirm cancellation — opens the standard CancelBookingModal so
          staff can edit the customer-supplied reason before final cancel.
          The customerReason prop renders a prominent read-only box of the
          original customer reason above the editable textarea so staff
          see exactly what the customer asked for (Task 6A). */}
      <CancelBookingModal
        open={confirmTarget !== null}
        encId={confirmTarget?.enc_id ?? null}
        reference={confirmTarget?.reference ?? null}
        initialReason={confirmTarget?.cancellation_reason ?? undefined}
        customerReason={confirmTarget?.cancellation_reason ?? null}
        onClose={() => setConfirmTarget(null)}
        onCancelled={async () => {
          await queryClient.invalidateQueries({ queryKey: queryKeys.cancellations.list({}) });
          await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
          setConfirmTarget(null);
        }}
      />

      {/* Reject cancellation — opens the DeclineCancellationModal so staff
          can optionally notify the customer with a custom message. */}
      <DeclineCancellationModal
        open={rejectTarget !== null}
        bookingEncId={rejectTarget?.enc_id ?? null}
        bookingReference={rejectTarget?.reference ?? null}
        customerName={rejectTarget?.name ?? null}
        onClose={() => setRejectTarget(null)}
        onSuccess={() => void handleDeclineSuccess()}
      />

      {/* Reinstate cancelled booking — opens the ReinstateBookingModal so
          staff can revive a cancelled booking with a fresh schedule. */}
      <ReinstateBookingModal
        open={reinstateTarget !== null}
        booking={reinstateTarget}
        onClose={() => setReinstateTarget(null)}
        onSuccess={() => void handleReinstateSuccess()}
      />
    </div>
  );
}
